// src/components/UpgradeModal.tsx
import { Trans, useTranslation } from "react-i18next";
import React, { useEffect, useRef, useState } from "react";
import {
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { Button } from "./Button";
import { createIntroTrialCheckout, createPaidCheckout, confirmPaidCheckout, openPortal } from "../api/billing";
import { A } from "../styles/appleClasses";

// Release-controlled acquisition policy. Opening this requires a reviewed deploy.
export const acquisitionPolicy = { paused: true };
export const paidAcquisitionPolicy = { paused: false };
const PAUSE_MESSAGE = "Pro upgrades are temporarily unavailable.";

const DEFAULT_LABELS = {
  "title": "Upgrade to Pro",
  "price": "HK$60/month",
  "description": "Everything in Basic, plus receipt scanning, invoice automation, branded invoices, and team access.",
  "staff": "Invite up to 3 staff members",
  "emails": "Send automatic invoice emails, up to 300 per month",
  "whatsapp": "WhatsApp-ready invoice sharing",
  "prioritySupport": "Priority email support",
  "branding": "Add a company logo and custom invoice footnote",
  "receiptScanner": "Scan receipts and bills. Review and enter. Up to 200 scans per month, with the original document saved for reference.",
  "embeddedLinks": "Include a Pay Link on invoices where supported",
  "fees": "Nimos charges 0.1% on successful online payments through Nimos Pay Links or the Stripe integration—HK$0.10 for every HK$100 paid. Stripe processing fees apply separately.",
  "gatewayHelp": "Online payments need Stripe. Connect it in Settings → Payment methods. Cash and bank transfers work without it.",
  "enterCode": "Enter Code",
  "invitationCode": "Invitation code",
  "notNow": "Not now",
  "opening": "Opening…",
  "startPro": "Start Pro",
  "alreadyPro": "Already Pro?",
  "billingPortal": "Open billing portal",
  "secure": "Secure checkout powered by Stripe",
  "timeout": "Couldn't confirm checkout. Please try again — your code still works.",
  "activating": "Your trial is being activated. Please refresh shortly.",
  "active": "Your Pro trial is already active.",
  "used": "This company has already used its introductory trial.",
  "ineligible": "This company is not eligible for an introductory trial.",
  "invalidCode": "This invitation code is invalid or cannot be used with this account.",
  "startTrialFailed": "Could not start the introductory trial.",
  "checkoutFailed": "Could not open checkout. Please try again.",
  "missingPortalUrl": "No portal URL returned.",
  "portalFailed": "Could not open billing portal. Check your Stripe portal config.",
  "dismiss": "Dismiss"
};
type RichUpgradeLabel = "staff" | "emails" | "receiptScanner" | "embeddedLinks" | "gatewayHelp";
export type UpgradeLabels = Partial<Omit<typeof DEFAULT_LABELS, RichUpgradeLabel>> & Partial<Record<RichUpgradeLabel, React.ReactNode>>;
type LocalOutcome = { code: keyof typeof DEFAULT_LABELS };
class LocalUpgradeError extends Error {
  constructor(readonly code: keyof typeof DEFAULT_LABELS) { super(DEFAULT_LABELS[code]); }
}

type Props = {
  open: boolean;
  onClose: () => void;
  labels?: UpgradeLabels;
  closeLabel?: string;
};

type ToastKind = "error" | "success" | "info";
type ToastState = { kind: ToastKind; msg: string | LocalOutcome } | null;

// Bounded, checkout-attempt-local timeout: a stuck/never-settling checkout
// request must not leave the modal indefinitely busy. This proves only that
// the browser never got a result -- it does not prove the backend failed
// (the claim reservation or Stripe Checkout Session may already exist), so
// it is surfaced as an ambiguous "please retry" message, never as an error.
const CHECKOUT_TIMEOUT_MS = 15000;

export default function UpgradeModal({ open, onClose, labels: displayLabels, closeLabel }: Props) {
  const { t } = useTranslation();
  const labels = { ...DEFAULT_LABELS,
    title: t("sales.upgrade.title", { defaultValue: DEFAULT_LABELS.title }),
    price: t("sales.upgrade.price", { defaultValue: DEFAULT_LABELS.price }),
    description: t("sales.upgrade.description", { defaultValue: DEFAULT_LABELS.description }),
    whatsapp: t("sales.upgrade.whatsapp", { defaultValue: DEFAULT_LABELS.whatsapp }),
    prioritySupport: t("sales.upgrade.prioritySupport", { defaultValue: DEFAULT_LABELS.prioritySupport }),
    branding: t("sales.upgrade.branding", { defaultValue: DEFAULT_LABELS.branding }),
    fees: t("sales.upgrade.fees", { defaultValue: DEFAULT_LABELS.fees }),
    enterCode: t("sales.upgrade.enterCode", { defaultValue: DEFAULT_LABELS.enterCode }),
    invitationCode: t("sales.upgrade.invitationCode", { defaultValue: DEFAULT_LABELS.invitationCode }),
    notNow: t("sales.upgrade.notNow", { defaultValue: DEFAULT_LABELS.notNow }),
    opening: t("sales.upgrade.opening", { defaultValue: DEFAULT_LABELS.opening }),
    startPro: t("sales.upgrade.startPro", { defaultValue: DEFAULT_LABELS.startPro }),
    alreadyPro: t("sales.upgrade.alreadyPro", { defaultValue: DEFAULT_LABELS.alreadyPro }),
    billingPortal: t("sales.upgrade.billingPortal", { defaultValue: DEFAULT_LABELS.billingPortal }),
    secure: t("sales.upgrade.secure", { defaultValue: DEFAULT_LABELS.secure }),
    timeout: t("sales.upgrade.timeout", { defaultValue: DEFAULT_LABELS.timeout }),
    activating: t("sales.upgrade.activating", { defaultValue: DEFAULT_LABELS.activating }),
    active: t("sales.upgrade.active", { defaultValue: DEFAULT_LABELS.active }),
    used: t("sales.upgrade.used", { defaultValue: DEFAULT_LABELS.used }),
    ineligible: t("sales.upgrade.ineligible", { defaultValue: DEFAULT_LABELS.ineligible }),
    invalidCode: t("sales.upgrade.invalidCode", { defaultValue: DEFAULT_LABELS.invalidCode }),
    startTrialFailed: t("sales.upgrade.startTrialFailed", { defaultValue: DEFAULT_LABELS.startTrialFailed }),
    checkoutFailed: t("sales.upgrade.checkoutFailed", { defaultValue: DEFAULT_LABELS.checkoutFailed }),
    missingPortalUrl: t("sales.upgrade.missingPortalUrl", { defaultValue: DEFAULT_LABELS.missingPortalUrl }),
    portalFailed: t("sales.upgrade.portalFailed", { defaultValue: DEFAULT_LABELS.portalFailed }),
    dismiss: t("sales.upgrade.dismiss", { defaultValue: DEFAULT_LABELS.dismiss }),
    ...displayLabels };
  const panelRef = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState<"none" | "checkout" | "portal">("none");
  const [invitationCode, setInvitationCode] = useState("");

  // Attempt-ownership bookkeeping for handleStartPro's timeout (see below).
  // `attemptIdRef` identifies which Start Pro attempt currently owns the
  // modal; `aliveRef` tracks whether the component is still mounted;
  // `activeAttemptRef` holds the in-flight attempt's controller/timer so a
  // later attempt can fully replace it and so unmount can abort/clear it.
  const attemptIdRef = useRef(0);
  const aliveRef = useRef(true);
  const activeAttemptRef = useRef<{
    controller: AbortController;
    timerId: number;
  } | null>(null);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (activeAttemptRef.current) {
        window.clearTimeout(activeAttemptRef.current.timerId);
        activeAttemptRef.current.controller.abort();
        activeAttemptRef.current = null;
      }
    };
  }, []);

  // Toast state
  const [toast, setToast] = useState<ToastState>(null);
  const toastTimerRef = useRef<number | null>(null);
  const showToast = (msg: string | LocalOutcome, kind: ToastKind = "error") => {
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    setToast({ kind, msg });
    toastTimerRef.current = window.setTimeout(
      () => setToast(null),
      3200,
    ) as unknown as number;
  };

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!open) {
      setInvitationCode("");
      ++attemptIdRef.current;
      if (activeAttemptRef.current) {
        window.clearTimeout(activeAttemptRef.current.timerId);
        activeAttemptRef.current.controller.abort();
        activeAttemptRef.current = null;
      }
      setBusy("none");
    }
  }, [open]);

  // Keep scroll gestures inside the modal and restore the page exactly as it was.
  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Focus first button on open
  useEffect(() => {
    if (open)
      panelRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
  }, [open]);

  const handleStartTrial = async () => {
    if (acquisitionPolicy.paused) return;
    const attemptId = ++attemptIdRef.current;
    const isCurrentAttempt = () =>
      aliveRef.current && attemptIdRef.current === attemptId;

    // A later attempt fully replaces the active controller/timer reference;
    // defensively clear any prior one here too (in practice unreachable
    // while `busy` gates re-entry, but keeps this attempt's ownership of
    // activeAttemptRef unambiguous regardless).
    if (activeAttemptRef.current) {
      window.clearTimeout(activeAttemptRef.current.timerId);
      activeAttemptRef.current.controller.abort();
      activeAttemptRef.current = null;
    }

    const controller = new AbortController();
    // `settled` is local to this attempt's own closure: whichever of {the
    // 15s timer, the awaited request} finishes first "wins" and is the only
    // one allowed to touch toast/busy state for this attempt. This is what
    // guarantees the bounded wait even if the underlying request never
    // actually settles (abort() is best-effort, not a guarantee).
    let settled = false;

    const timerId = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      controller.abort();
      if (isCurrentAttempt()) {
        showToast({ code: "timeout" }, "info");
        setBusy("none");
      }
    }, CHECKOUT_TIMEOUT_MS);

    activeAttemptRef.current = { controller, timerId };

    try {
      setToast(null);
      setBusy("checkout");
      const trimmedCode = invitationCode.trim();
      const result = await createIntroTrialCheckout(
        trimmedCode || undefined,
        controller.signal,
      );

      // Already finalized as a timeout (or already superseded) -- a late
      // success must not retroactively navigate or otherwise take effect.
      if (settled) return;
      settled = true;
      window.clearTimeout(timerId);
      if (!isCurrentAttempt()) return;

      if (
        (result.status === "created" || result.status === "reused") &&
        result.url
      ) {
        window.location.assign(result.url);
        return;
      }

      if (result.status === "awaiting_activation") {
        showToast(
          { code: "activating" },
          "info",
        );
        return;
      }

      if (result.status === "already_activated") {
        showToast({ code: "active" }, "info");
        return;
      }

      if (result.status === "prior_trial_used") {
        showToast({ code: "used" });
        return;
      }

      if (result.status === "not_eligible") {
        showToast({ code: "ineligible" });
        return;
      }

      if (result.status === "invalid_code") {
        showToast(
          { code: "invalidCode" },
        );
        return;
      }

      throw new LocalUpgradeError("startTrialFailed");
    } catch (e: any) {
      // Already finalized as a timeout (or already superseded) -- a late
      // rejection (including the abort our own timer just triggered) must
      // not produce a second, generic-error toast on top of the timeout
      // toast, and must not touch a newer attempt's state.
      if (settled) return;
      settled = true;
      window.clearTimeout(timerId);
      if (!isCurrentAttempt()) return;

      console.error("createIntroTrialCheckout failed", e);
      showToast(e instanceof LocalUpgradeError ? { code: e.code } : e?.message ?? { code: "checkoutFailed" });
    } finally {
      if (activeAttemptRef.current?.controller === controller) {
        activeAttemptRef.current = null;
      }
      if (settled && isCurrentAttempt()) {
        setBusy("none");
      }
    }
  };

  const paidKey = useRef<string | null>(null);
  const [paidStatus, setPaidStatus] = useState<string>("eligible");
  useEffect(() => {
    if (!open || paidAcquisitionPolicy.paused) return;
    let live = true;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), CHECKOUT_TIMEOUT_MS);
    confirmPaidCheckout(undefined, controller.signal).then(result => {
      if (live && !controller.signal.aborted) setPaidStatus(result.status);
    }).catch(() => { if (live) setPaidStatus("pending_confirmation"); });
    return () => { live = false; controller.abort(); window.clearTimeout(timeout); };
  }, [open]);
  const handleStartPro = async () => {
    if (paidAcquisitionPolicy.paused || busy !== "none") return;
    const id = ++attemptIdRef.current;
    const controller = new AbortController();
    let settled = false;
    const timerId = window.setTimeout(() => {
      if (settled) return;
      settled = true; controller.abort();
      if (aliveRef.current && attemptIdRef.current === id) {
        setBusy("none"); setPaidStatus("pending_confirmation");
      }
    }, CHECKOUT_TIMEOUT_MS);
    activeAttemptRef.current = { controller, timerId };
    setBusy("checkout");
    try {
      let result = await confirmPaidCheckout(undefined, controller.signal);
      if (settled || !aliveRef.current || attemptIdRef.current !== id) return;
      if (["eligible", "subscribe_again", "expired", "failed"].includes(result.status)) {
        if (["subscribe_again", "expired", "failed"].includes(result.status)) paidKey.current = null;
        if (!paidKey.current) paidKey.current = crypto.randomUUID();
        result = await createPaidCheckout(paidKey.current, controller.signal);
      }
      if (settled || !aliveRef.current || attemptIdRef.current !== id) return;
      setPaidStatus(result.status);
      if (result.status === "checkout_open" && result.url) window.location.assign(result.url);
      if (result.status === "manage_subscription" || result.status === "pro") {
        const portal = await openPortal();
        if (!settled && aliveRef.current && attemptIdRef.current === id) window.location.assign(portal.url);
      }
    } catch {
      if (!settled && aliveRef.current && attemptIdRef.current === id) setPaidStatus("pending_confirmation");
    } finally {
      settled = true; window.clearTimeout(timerId);
      if (activeAttemptRef.current?.controller === controller) activeAttemptRef.current = null;
      if (aliveRef.current && attemptIdRef.current === id) setBusy("none");
    }
  };

  if (!open) return null;

  const toastStyles: Record<
    ToastKind,
    { wrap: string; icon: string; text: string }
  > = {
    error: {
      wrap: "border-red-200 bg-red-50/40",
      icon: "text-red-600",
      text: "text-red-800",
    },
    success: {
      wrap: "border-emerald-200 bg-emerald-50/40",
      icon: "text-emerald-600",
      text: "text-emerald-800",
    },
    info: {
      wrap: "border-sky-200 bg-sky-50/40",
      icon: "text-sky-600",
      text: "text-sky-800",
    },
  };

  const closeBtnCls =
    `${A.iconBtn} absolute right-3 top-3 inline-flex items-center justify-center ` +
    "text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition " +
    "focus:outline-none focus:ring-2 focus:ring-appleblueD";

  return (
    <div
      className="fixed inset-0 z-[1000] overflow-y-auto overscroll-contain"
      style={{ height: "100dvh", WebkitOverflowScrolling: "touch" }}
      data-testid="upgrade-modal-viewport"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div
        className="pointer-events-none fixed inset-0 bg-black/40"
        aria-hidden
      />

      {/* Panel container */}
      <div className="relative flex min-h-full items-start justify-center px-2 py-4 sm:items-center sm:px-4">
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="upgrade-title"
          className={["relative w-full max-w-xl", A.card, "shadow-md"].join(
            " ",
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <button onClick={onClose} aria-label={closeLabel ?? t("directory.common.close")} className={closeBtnCls}>
            <XMarkIcon className="h-5 w-5" />
          </button>

          {/* Body */}
          <div className="px-6 pt-6 pb-5">
            <h2 id="upgrade-title" className={`${A.title} text-center`}>
              {labels.title}
            </h2>

            <p className="mt-2 text-center">
              <span className="text-[22px] font-semibold text-gray-800">
                {labels.price}
              </span>
            </p>

            <p className={`${A.body} text-gray-500 mt-2 text-center`}>
              {labels.description}
            </p>

            <div className="mt-5 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4">
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <CheckCircleIcon className="h-4 w-4 text-appleblue mt-0.5 shrink-0" />
                  <span className={A.body}>
                    {displayLabels?.receiptScanner ?? <Trans i18nKey="sales.upgrade.receiptScanner" components={{ emphasis0: <span className="font-medium text-gray-700" />, emphasis1: <span className="font-medium text-gray-600" /> }} />}
                  </span>
                </li>

                <li className="flex items-start gap-2">
                  <CheckCircleIcon className="h-4 w-4 text-appleblue mt-0.5 shrink-0" />
                  <span className={A.body}>
                    {displayLabels?.emails ?? <Trans i18nKey="sales.upgrade.emails" components={{ emphasis0: <span className="font-medium text-gray-700" />, emphasis1: <span className="font-medium text-gray-600" /> }} />}
                  </span>
                </li>

                <li className="flex items-start gap-2">
                  <CheckCircleIcon className="h-4 w-4 text-appleblue mt-0.5 shrink-0" />
                  <span className={A.body}>
                    <span className="font-medium text-gray-700">
                      {labels.whatsapp}
                    </span>
                  </span>
                </li>

                <li className="flex items-start gap-2">
                  <CheckCircleIcon className="h-4 w-4 text-appleblue mt-0.5 shrink-0" />
                  <span className={A.body}>
                    <span className="font-medium text-gray-700">
                      {labels.branding}
                    </span>
                  </span>
                </li>

                <li className="flex items-start gap-2">
                  <CheckCircleIcon className="h-4 w-4 text-appleblue mt-0.5 shrink-0" />
                  <span className={A.body}>
                    {displayLabels?.embeddedLinks ?? <Trans i18nKey="sales.upgrade.embeddedLinks" components={{ emphasis0: <span className="font-medium text-gray-700" />, emphasis1: <span className="font-medium text-gray-600" /> }} />}
                  </span>
                </li>

                <li className="flex items-start gap-2">
                  <CheckCircleIcon className="h-4 w-4 text-appleblue mt-0.5 shrink-0" />
                  <span className={A.body}>
                    {displayLabels?.staff ?? <Trans i18nKey="sales.upgrade.staff" components={{ emphasis0: <span className="font-medium text-gray-700" />, emphasis1: <span className="font-medium text-gray-600" /> }} />}
                  </span>
                </li>

                <li className="flex items-start gap-2">
                  <CheckCircleIcon className="h-4 w-4 text-appleblue mt-0.5 shrink-0" />
                  <span className={A.body}>{labels.prioritySupport}</span>
                </li>
              </ul>
            </div>

            <p className="mt-3 text-xs text-gray-600 leading-5">
              {labels.fees}
            </p>

            <div className="mt-3 space-y-2 text-xs leading-5 text-gray-600">
              <p>{t("sales.upgrade.scanNotice")}</p>
              <p>{t("sales.upgrade.supportTarget")}</p>
              <p>{t("sales.upgrade.businessDays")}</p>
              <p>{t("sales.upgrade.supportScope")}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                <a className="text-appleblue underline underline-offset-2 break-all" href="mailto:support@nimos.cloud">
                  {t("sales.upgrade.supportEmail")}: support@nimos.cloud
                </a>
                <a className="text-appleblue underline underline-offset-2" href="https://www.nimos.cloud/learn" target="_blank" rel="noopener noreferrer">
                  {t("sales.upgrade.videoGuides")}
                </a>
              </div>
            </div>

            {/* Stripe clarification */}
            <p className="mt-3 text-xs text-gray-500 leading-snug text-center">
              {displayLabels?.gatewayHelp ?? <Trans i18nKey="sales.upgrade.gatewayHelp" components={{ emphasis0: <span className="font-medium text-gray-700" />, emphasis1: <span className="font-medium text-gray-600" /> }} />}
            </p>
          </div>

          {/* Footer actions */}
          <div className="px-6 pb-6">
            {acquisitionPolicy.paused && paidAcquisitionPolicy.paused && (
              <p role="status" className="mb-4 text-[13px] text-gray-600">
                {t("sales.upgrade.paused", { defaultValue: PAUSE_MESSAGE })}
              </p>
            )}
            <div className="mb-4 rounded-2xl bg-appleblue/10 px-3 py-3">
              <label
                htmlFor="intro-trial-invitation-code"
                className="text-[13px] font-normal text-gray-600"
              >
                {labels.enterCode}
              </label>
              <input
                id="intro-trial-invitation-code"
                name="invitation_code"
                type="text"
                value={invitationCode}
                onChange={(event) => {
                  setInvitationCode(event.target.value);
                  if (toast?.kind === "error") setToast(null);
                }}
                disabled={acquisitionPolicy.paused || busy !== "none"}
                autoComplete="off"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="done"
                onKeyDown={(event) => {
                  if (!acquisitionPolicy.paused && event.key === "Enter" && busy === "none") {
                    event.preventDefault();
                    void handleStartTrial();
                  }
                }}
                className={`${A.input} mt-2 bg-white text-gray-700 disabled:bg-gray-50 disabled:text-gray-400 disabled:opacity-100`}
                placeholder={labels.invitationCode}
              />
            </div>

            {paidStatus === "pending_confirmation" && <p role="status" className="mb-3 text-sm">
              {t("sales.upgrade.paidPending", { defaultValue: "Payment confirmation is pending. Check its status before starting another checkout." })}
            </p>}
            {paidStatus === "pro" && <p role="status">{t("sales.upgrade.paidActive", { defaultValue: "Your company has Pro access." })}</p>}
            {!acquisitionPolicy.paused && <button type="button" disabled={busy !== "none"} onClick={handleStartTrial}>
              {t("sales.upgrade.useInvitation", { defaultValue: "Use invitation" })}
            </button>}
            <div
              className="flex flex-col justify-end gap-3 sm:flex-row"
              data-testid="upgrade-modal-actions"
            >
              <Button
                variant="secondary"
                onClick={onClose}
                minWidth="0"
                className="w-full sm:w-auto sm:min-w-[120px]"
              >
                {labels.notNow}
              </Button>

              <Button
                variant="primary"
                onClick={handleStartPro}
                disabled={paidAcquisitionPolicy.paused || busy !== "none"}
                minWidth="0"
                className="w-full sm:w-auto sm:min-w-[140px]"
              >
                {busy === "checkout" ? labels.opening : paidStatus === "subscribe_again"
                  ? t("sales.upgrade.subscribeAgain", { defaultValue: "Subscribe again" })
                  : ["manage_subscription", "pro"].includes(paidStatus) ? labels.billingPortal
                  : paidStatus === "pending_confirmation" ? t("sales.upgrade.checkPayment", { defaultValue: "Check payment status" }) : labels.startPro}
              </Button>
            </div>

            <div className="mt-3 text-xs text-gray-500 text-center">
              {labels.alreadyPro}{" "}
              <button
                className="text-appleblue hover:text-appleblueD transition underline underline-offset-2
                           focus:outline-none focus:ring-2 focus:ring-appleblueD rounded"
                onClick={async () => {
                  try {
                    setBusy("portal");
                    const { url } = await openPortal();
                    if (!url) throw new LocalUpgradeError("missingPortalUrl");
                    window.location.assign(url);
                  } catch (e: any) {
                    console.error("openPortal failed", e);
                    showToast(
                      e instanceof LocalUpgradeError ? { code: e.code } : e?.message ?? { code: "portalFailed" },
                    );
                  } finally {
                    setBusy("none");
                  }
                }}
              >
                {labels.billingPortal}
              </button>
            </div>

            {!paidAcquisitionPolicy.paused && (
              <div className="mt-2 text-[11px] text-gray-400 text-center">
                {labels.secure}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Small toast */}
      {toast && (
        <div
          className="pointer-events-none fixed inset-x-0 bottom-6 z-[1100] flex justify-center px-4"
          role="status"
          aria-live="polite"
        >
          <div
            className={[
              "pointer-events-auto flex items-start gap-2 max-w-md w-full",
              "border shadow-md",
              "px-4 py-3",
              "rounded-2xl bg-white",
              A.body,
              toastStyles[toast.kind].wrap,
            ].join(" ")}
          >
            {toast.kind === "success" ? (
              <CheckCircleIcon
                className={`h-5 w-5 ${toastStyles[toast.kind].icon} mt-0.5`}
              />
            ) : (
              <ExclamationTriangleIcon
                className={`h-5 w-5 ${toastStyles[toast.kind].icon} mt-0.5`}
              />
            )}

            <div className={`flex-1 ${toastStyles[toast.kind].text}`}>
              {typeof toast.msg === "string" ? toast.msg : labels[toast.msg.code]}
            </div>

            <button
              className="ml-2 -mr-1 h-10 w-10 inline-flex items-center justify-center rounded-xl
                         text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition
                         focus:outline-none focus:ring-2 focus:ring-appleblueD"
              onClick={() => setToast(null)}
              aria-label={labels.dismiss}
              type="button"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
